import React, { useRef, useState } from "react";

import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from "@mui/material";

import type { SxProps } from "@mui/material";

import { lightNeutral, lightPrimary } from "src/colors";
import { ExpandMore } from "@mui/icons-material";
import { OrganizationLanguage } from "src/types/enum/organization-language";

interface LanguagePopoverProps {
  language: string;
  onLanguageChange: (newLanguage: "en" | "pl") => void;
  sx?: SxProps;
  textMode?: boolean;
}

const languageOptions = {
  en: {
    icon: "/static/en_flag.svg",
    label: "English",
    shortcut: "EN",
  },
  pl: {
    icon: "/static/pl_flag.svg",
    label: "Polish",
    shortcut: "PL",
  },
};

export const LanguagePopover: React.FC<LanguagePopoverProps> = ({
  language,
  onLanguageChange,
  textMode,
  ...other
}) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLanguageChange = (newLanguage: "en" | "pl"): void => {
    onLanguageChange(newLanguage);
    setOpen(false);
  };

  const selectedOption = languageOptions[language as OrganizationLanguage];

  return (
    <>
      <IconButton
        onClick={handleOpen}
        ref={anchorRef}
        {...other}
        sx={{
          boxShadow: textMode ? lightPrimary.borders : "transparent",
          background: textMode ? lightNeutral[100] : "transparent",
          borderRadius: textMode ? "6px" : "inherit",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 20,
            width: textMode ? 40 : 20,
            "& img": {
              width: "100%",
            },
          }}
        >
          {textMode ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="subtitle2">
                {selectedOption.shortcut}
              </Typography>
              <ExpandMore
                fontSize="small"
                style={{
                  rotate: open ? "180deg" : "0deg",
                  transition: "all 0.1s linear",
                }}
              />
            </Box>
          ) : (
            <img alt={selectedOption.label} src={selectedOption.icon} />
          )}
        </Box>
      </IconButton>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: "center",
          vertical: "bottom",
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: textMode ? 50 : 240 },
        }}
      >
        {Object.keys(languageOptions).map((option) => (
          <MenuItem
            onClick={() => handleLanguageChange(option as OrganizationLanguage)}
            key={option}
          >
            {textMode ? null : (
              <ListItemIcon>
                <Box
                  sx={{
                    display: "flex",
                    height: 20,
                    width: 20,
                    "& img": {
                      width: "100%",
                    },
                  }}
                >
                  <img
                    alt={languageOptions[option as OrganizationLanguage].label}
                    src={languageOptions[option as OrganizationLanguage].icon}
                  />
                </Box>
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                <Typography color="textPrimary" variant="subtitle2">
                  {textMode
                    ? languageOptions[option as OrganizationLanguage].shortcut
                    : languageOptions[option as OrganizationLanguage].label}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Popover>
    </>
  );
};
